import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import closeFullScreen from "../../images/x-square.svg";
import fullScreen from "../../images/fullscreen.svg";
import fullScreenExit from "../../images/fullscreen-exit.svg";
import { useAtom } from "jotai";
import { projectAtomList } from "../../atoms";

const DraggableModal = ({ isOpen, onClose, projectKind, assetDetails }) => {
  const [isMiniMax, setIsMiniMax] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 268, y: 142 });
  const modalRef = useRef(null);
  const [updatedComponents] = useAtom(projectAtomList);

  const handleMouseMove = useCallback(
    (e) => {
      if (isDragging) {
        setPosition((prevPosition) => ({
          x: prevPosition.x + e.movementX,
          y: prevPosition.y + e.movementY,
        }));
      }
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    modalRef.current.style.cursor = "grab";
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleClose = () => {
    document.getElementsByTagName("se-container")[0].style.scrollbarWidth = "auto";
    onClose();
    setIsMiniMax(false);
    setPosition({ x: 268, y: 142 });
  };

  if (!isOpen) return null;

  const handleMinMax = () => {
    setIsMiniMax((prev) => !prev);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    modalRef.current.style.cursor = "grabbing";
  };

  const getComponents = () => {
    const derAssetsList = {
      Utility: 0,
      Genset: 1,
      BESS: 2,
      Inverter: 3,
      Load: 4,
      "Circuit Breaker": 5,
      "Power Meter": 6,
    };

    const sortedComponents = updatedComponents?.updateComponents
      .slice()
      .sort((a, b) => {
        const aIndex =
          derAssetsList[a.componentType] !== undefined
            ? derAssetsList[a.componentType]
            : Infinity;
        const bIndex =
          derAssetsList[b.componentType] !== undefined
            ? derAssetsList[b.componentType]
            : Infinity;
        return aIndex - bIndex;
      });

    return sortedComponents;
  };

  const mapping = {
    Utility: "Utility",
    Genset: "Genset",
    BESS: "Battery Energy Storage System",
    Inverter: (name) => name + " - Inverter",
    Load: (name) => name,
    "Circuit Breaker": (name) => name + " - Circuit Breaker",
    "Power Meter": (name) => name + " - Metering",
    Hybrid: (name) => name,
    Contactor:(name) => name,
    EV_charging_station:(name) => name,
  };

  // Function to get the mapped name
  const getMappedName = (componentType, componentName) => {
    const mappedValue = mapping[componentType];
    return typeof mappedValue === "function"
      ? mappedValue(componentName)
      : mappedValue;
  };

  const getAssetConfig = (type) => {
    let utilityAttributes;

    try {
      utilityAttributes = JSON.parse(
        updatedComponents?.updateComponents.filter(
          (item) => item.componentType === type
        )[0].attributes
      );
    } catch (error) {
      console.error("Error parsing JSON:", error);
      utilityAttributes = null;
    }
    return utilityAttributes;
  };
  const getSelectedAssetDetails = (assetId) => {
    const assetDetailsData = assetDetails?.assets?.filter(
      (asset) => asset?.assetId === assetId
    );
    return assetDetailsData[0];
  };
  return (
    <div className="settings-modal-backdrop">
      <div
        ref={modalRef}
        className={`modal-draggable row card ${
          isMiniMax ? "maximized-draggable" : "minimized-draggable"
        }
          ${
            updatedComponents?.isSiteCreated &&
            updatedComponents?.projectStatus?.projectStatus === "COMPLETED"
              ? "top-214"
              : updatedComponents?.isSiteCreated === false &&
                updatedComponents?.projectStatus?.projectStatus !== "COMPLETED"
              ? ""
              : "top-197"
          }`}
        style={{
          top: isMiniMax ? "0" : position.y,
          left: isMiniMax ? "3px" : position.x,
        }}
        // onMouseDown={handleMouseDown}
        aria-labelledby="modal-title"
        role="dialog"
      >
        <div className="col-sm-12">
          <div className="modal-header-draggable row p-0">
            <div
              className="col-sm-12 d-flex justify-content-between align-items-center"
              style={{ height: "30px", backgroundColor: "rgb(61, 205, 88)" }}
            >
              <div
                id="modal-title"
                style={{ color: "var(--se-background-alternative)" }}
              >
                DERs Settings
              </div>
              <div>
                <button
                  type="button"
                  className="border-0 pr-2"
                  onClick={handleMinMax}
                  style={{ background: "transparent" }}
                >
                  <img
                    src={isMiniMax ? fullScreenExit : fullScreen}
                    alt="Toggle Full Screen"
                    title="Click to toggle full screen"
                    className="pointer"
                    width="15"
                    height="15"
                  />
                </button>
                <button
                  className="border-0 p-0"
                  onClick={handleClose}
                  style={{ background: "transparent" }}
                >
                  <img
                    src={closeFullScreen}
                    alt="Close Button"
                    title="Click to close"
                    className="pointer"
                    width="15"
                    height="15"
                  />
                </button>
              </div>
            </div>
          </div>
          <div
            className="modal-content-draggable row"
            style={{ height:isMiniMax?"100vh":"70vh", overflowY: "scroll" }}
          >
            <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 pb-3 ">
              <div
                className={`table-responsive table-responsive-scroll ${
                  isMiniMax ? "" : projectKind?.isA2E ? "" : " modal-height"
                }`}
                style={{
                  overflowY: `${isMiniMax ? "hidden" : "scroll"}, `,
                  scrollbarWidth: "none",
                }}
              >
                <div className="settings-table-header">
                  <table
                    className="table table-hover table-bordered"
                    id="job-table"
                  >
                    <thead>
                      <tr>
                        <th className="border-right" scope="col" width="30%">
                          Name
                        </th>
                        <th className="border-right" scope="col" width="20%">
                          Manufacturer Name
                        </th>
                        <th className="border-right" scope="col" width="20%">
                          Asset Type
                        </th>
                        <th className="border-right" scope="col" width="15%">
                          Asset Category
                        </th>
                        <th className="border-right" scope="col" width="15%">
                          Model
                        </th>
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="settings-table-body">
                  <table className="table table-bordered mb-0 sticky-header-table ">
                    <tbody>
                      {getComponents()?.filter(item=>{
                          if(item.componentType === "Circuit Breaker"&& JSON.parse(item.uiAttributes).cbRequired){
                            return item;
                            
                        }else if(item.componentType !== "Circuit Breaker"){
                            return item;
                        }
                      })?.map((der, index) => (
                        <tr key={index}>
                          <td width="30%">
                            {getMappedName(
                              der?.componentType,
                              der?.componentName
                            )}
                          </td>
                          <td width="20%">
                            {console.log(
                              "getSelectedAssetDetails(der?.assetId)",
                              getSelectedAssetDetails(der)
                            )}
                            {der?.asset?.manufacturer
                              ? der?.asset?.manufacturer?.toUpperCase()
                              : getSelectedAssetDetails(der?.assetId)
                                  ?.manufacturer
                              ? getSelectedAssetDetails(
                                  der?.assetId
                                )?.manufacturer.toUpperCase()
                              : "NA"}
                          </td>
                          <td width="20%">
                            {der?.asset?.assetType
                              ? der?.asset?.assetType
                              : getSelectedAssetDetails(der?.assetId)?.type
                                  ?.type
                              ? getSelectedAssetDetails(der?.assetId)?.type
                                  ?.type
                              : "NA"}
                          </td>
                          <td width="15%">
                            {der?.asset?.assetInfo
                              ? der?.asset?.assetInfo
                              : getSelectedAssetDetails(der?.assetId)?.assetInfo
                              ? getSelectedAssetDetails(der?.assetId)?.assetInfo
                              : "NA"}
                          </td>
                          <td width="15%">
                            {der?.asset?.communicationModel
                              ? der?.asset?.communicationModel
                              : getSelectedAssetDetails(der?.assetId)
                                  ?.communicationModel
                              ? getSelectedAssetDetails(der?.assetId)
                                  ?.communicationModel
                              : "NA"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <se-divider></se-divider>
              {/* Utility table */}
              {getAssetConfig("Utility") && (
                <div
                  className={`table-responsive table-responsive-scroll mt-3 mb-2`}
                  style={{
                    overflowY: `${isMiniMax ? "hidden" : "scroll"}`,
                    width: "50%",
                    scrollbarWidth: "none",
                  }}
                >
                  <div className="settings-table-header">
                    <table
                      className="table table-hover table-bordered"
                      id="job-table"
                    >
                      <thead>
                        <tr>
                          <th
                            className="border-right text-center"
                            scope="col"
                            width="50%"
                          >
                            Utility
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="settings-table-body">
                    <table className="table table-bordered mb-0 sticky-header-table ">
                      <tbody>
                        <tr>
                          <td width="30%">{"Max power available (kW)"}</td>
                          <td width="20%">
                            {getAssetConfig("Utility")?.maximum_available_power}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">{"CO2 Emission Rate (g/kWh)"}</td>
                          <td width="20%">
                            {getAssetConfig("Utility")?.utilityCO2}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Genset table */}
              {getAssetConfig("Genset") && (
                <div
                  className={`table-responsive table-responsive-scroll mt-3 mb-2`}
                  style={{
                    overflowY: `${isMiniMax ? "hidden" : "scroll"}`,
                    width: "50%",
                    scrollbarWidth: "none",
                  }}
                >
                  <div className="settings-table-header">
                    <table
                      className="table table-hover table-bordered"
                      id="job-table"
                    >
                      <thead>
                        <tr>
                          <th
                            className="border-right text-center"
                            scope="col"
                            width="50%"
                          >
                            Genset
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="settings-table-body">
                    <table className="table table-bordered mb-0 sticky-header-table ">
                      <tbody>
                        <tr>
                          <td width="30%">{"Max power available (kW)"}</td>
                          <td width="20%">
                            {getAssetConfig("Genset")?.max_power}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">{"CO2 Emission Rate (kg/MWh)"}</td>
                          <td width="20%">
                            {getAssetConfig("Genset")?.gensetCO2}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">{"Start-up duration (s)"}</td>
                          <td width="20%">
                            {getAssetConfig("Genset")?.starting_timer}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">{"Cooling duration (s)"}</td>
                          <td width="20%">
                            {getAssetConfig("Genset")?.cooling_timer}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">{"Min running duration (s)"}</td>
                          <td width="20%">
                            {getAssetConfig("Genset")?.minimum_running_period}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Battery Energy Storage System table */}
              {getAssetConfig("BESS") && (
                <div
                  className={`table-responsive table-responsive-scroll mt-3 mb-2`}
                  style={{
                    overflowY: `${isMiniMax ? "hidden" : "scroll"}`,
                    width: "50%",
                    scrollbarWidth: "none",
                  }}
                >
                  <div className="settings-table-header">
                    <table
                      className="table table-hover table-bordered"
                      id="job-table"
                    >
                      <thead>
                        <tr>
                          <th
                            className="border-right text-center"
                            scope="col"
                            width="50%"
                          >
                            Battery Energy Storage System
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="settings-table-body">
                    <table className="table table-bordered mb-0 sticky-header-table ">
                      <tbody>
                        <tr>
                          <td width="30%">{"Max power available (kW)"}</td>
                          <td width="20%">
                            {getAssetConfig("BESS")?.max_power}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">{"Capacity (kWh)"}</td>
                          <td width="20%">
                            {getAssetConfig("BESS")?.capacity}
                          </td>
                        </tr>
                        <tr>
                          <td width="30%">
                            {"SOC Max (% of battery capacity in kWh)"}
                          </td>
                          <td width="20%">{getAssetConfig("BESS")?.soc_max}</td>
                        </tr>
                        <tr>
                          <td width="30%">
                            {"SOC Min (% of battery capacity in kWh)"}
                          </td>
                          <td width="20%">{getAssetConfig("BESS")?.soc_min}</td>
                        </tr>
                        <tr>
                          <td width="30%">
                            {"SOC Nominal (% of battery capacity in kWh)"}
                          </td>
                          <td width="20%">
                            {getAssetConfig("BESS")?.soc_nominal_power}
                          </td>
                        </tr>
                        { 
                           <tr>
                          <td width="30%">
                            {"SOC Min Optimization(% of battery capacity in kWh)"}
                          </td>
                          <td width="20%">
                            {getAssetConfig("BESS")?.SoC_min_optimization}
                          </td>
                        </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!projectKind?.isA2E && (
                <div
                  className={`table-responsive table-responsive-scroll mt-3 mb-2`}
                  style={{
                    overflowY: `${isMiniMax ? "hidden" : "scroll"}, `,
                    scrollbarWidth: "none",
                    width: "50%",
                  }}
                >
                  <div className="settings-table-header">
                    <table
                      className="table table-hover table-bordered"
                      id="job-table"
                    >
                      <thead>
                        <tr>
                          <th className="border-right" scope="col" width="30%">
                            Photo Voltaic
                          </th>
                          <th className="border-right" scope="col" width="20%">
                            Max power available (kW)
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="settings-table-body">
                    <table className="table table-bordered mb-0 sticky-header-table ">
                      <tbody>
                        {getComponents()
                          ?.filter((item) => item.componentType === "Inverter")
                          .map((der, index) => (
                            <tr key={index}>
                              <td width="30%">
                                {getMappedName(
                                  der?.componentType,
                                  der?.componentName
                                )}
                              </td>
                              <td width="20%">
                                {
                                  JSON.parse(
                                    getComponents()?.filter(
                                      (item) =>
                                        item.componentType === "Inverter"
                                    )[index].attributes
                                  )?.max_power
                                }
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
               { (
                <div
                  className={`table-responsive table-responsive-scroll mt-3 mb-2`}
                  style={{
                    overflowY: `${isMiniMax ? "hidden" : "scroll"}, `,
                    scrollbarWidth: "none",
                    width: "50%",
                  }}
                >
                  <div className="settings-table-header">
                    <table
                      className="table table-hover table-bordered"
                      id="job-table"
                    >
                      <thead>
                        <tr>
                          <th className="border-right" scope="col" width="30%">
                            Loads
                          </th>
                          <th className="border-right" scope="col" width="20%">
                            Max power available (kW)
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="settings-table-body">
                    <table className="table table-bordered mb-0 sticky-header-table ">
                      <tbody>
                        {getComponents()
                          ?.filter((item) => item.componentType === "Load")
                          .map((der, index) => (
                            <tr key={index}>
                              <td width="30%">
                                {getMappedName(
                                  der?.componentType,
                                  der?.componentName
                                )}
                              </td>
                              <td width="20%">
                                {
                                  JSON.parse(
                                    getComponents()?.filter(
                                      (item) =>
                                        item.componentType === "Load"
                                    )[index]?.attributes
                                  )?.maximum_available_power
                                }
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DraggableModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DraggableModal;
